import { useApolloClient } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { NotificationPreferenceScope } from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  HAS_OPEN_NOTIFICATIONS_TO_DELIVER,
  MY_NOTIFICATION_PREFERENCE_CATALOG,
  MY_SUBSCRIBED_ORGANIZATIONS,
  SET_NOTIFICATION_PREFERENCES,
} from "./notificationPreferenceDocuments";
import { REFRESH_FAILED_COPY, timeZoneEntryForSave } from "./preferencesPageState";
import {
  buildSavePayload,
  dirtyToggles,
  draftKey,
  newlyDeliveringKinds,
  setDraftEmailChoice,
  setDraftSectionChannel,
  setDraftToggle,
  useNotificationPreferenceDraft,
  type DraftCatalog,
  type DraftToggle,
  type NotificationPreferenceDraft,
  type NotificationPreferenceDraftController,
} from "./useNotificationPreferenceDraft";

/**
 * A staff member's settings for one organization, or for every one, saved change by
 * change.
 *
 * Only the toggles a change moved are blocked while it saves. Changes go one at a
 * time — the open-conditions check, its confirmation, then the write — so they land
 * in order and each gets its own confirmation. The catalog is refetched once none
 * are left. The switches are not reset from a refetch while any change is still
 * pending, because that answer may predate it and would put the switch back.
 */

const SCOPE = NotificationPreferenceScope.My;

/** What one change moved, and where those toggles were before it. */
type Change = {
  toggles: DraftToggle[];
  previous: DraftToggle[];
  /** The whole draft after the change, which the time zone entry is read from. */
  draft: NotificationPreferenceDraft;
  /** The whole draft before it, to tell what the change newly turns on. */
  before: NotificationPreferenceDraft;
};

/** Asks the reader, and answers once they have. */
type Confirm = () => Promise<boolean>;

function keysOf(toggles: readonly DraftToggle[]): string[] {
  return toggles.map((toggle) => draftKey(toggle.kind, toggle.channel));
}

function usePendingKeys() {
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set());
  const hold = useCallback((keys: readonly string[]) => {
    setPendingKeys((current) => new Set([...current, ...keys]));
  }, []);
  const release = useCallback((keys: readonly string[]) => {
    setPendingKeys((current) => new Set([...current].filter((key) => !keys.includes(key))));
  }, []);
  return { pendingKeys, hold, release };
}

/** Whether turning these on would deliver conditions that are already open. */
function useOpenConditionsCheck(tenantId: string | null, catalog: DraftCatalog | undefined) {
  const client = useApolloClient();
  return useCallback(
    async (next: NotificationPreferenceDraft, previous: NotificationPreferenceDraft) => {
      // With no organization, this asks across every one the Default reaches.
      if (!catalog) {
        return false;
      }
      const kinds = newlyDeliveringKinds(
        catalog.sections,
        next,
        previous,
        catalog.channelParentSwitches
      );
      if (kinds.length === 0) {
        return false;
      }
      try {
        const { data } = await client.query({
          query: HAS_OPEN_NOTIFICATIONS_TO_DELIVER,
          variables: { tenantId, kinds, scope: SCOPE },
          fetchPolicy: "network-only",
        });
        return data.hasOpenNotificationsToDeliver;
      } catch {
        return false;
      }
    },
    [catalog, client, tenantId]
  );
}

/** Saves changes one after another, refetching once the queue runs dry. */
function useSaveQueue({
  tenantId,
  catalog,
  apply,
  release,
  confirm,
}: {
  tenantId: string | null;
  catalog: DraftCatalog | undefined;
  apply: (toggles: readonly DraftToggle[]) => void;
  release: (keys: readonly string[]) => void;
  confirm: Confirm;
}) {
  const client = useApolloClient();
  const alerts = useAlerts();
  const opensConditions = useOpenConditionsCheck(tenantId, catalog);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const waiting = useRef(0);
  const awaitingRefresh = useRef<string[]>([]);

  const write = useCallback(
    async (change: Change): Promise<boolean> => {
      try {
        await client.mutate({
          mutation: SET_NOTIFICATION_PREFERENCES,
          variables: {
            tenantId,
            input: buildSavePayload(
              change.toggles,
              SCOPE,
              timeZoneEntryForSave(
                change.toggles,
                catalog?.channelParentSwitches,
                change.draft,
                SCOPE
              )
            ),
          },
        });
        return true;
      } catch (error) {
        alerts.error(error instanceof Error ? error.message : "Your change could not be saved.");
        return false;
      }
    },
    [alerts, catalog?.channelParentSwitches, client, tenantId]
  );

  const refresh = useCallback(async () => {
    try {
      // The list too, whose markers say which organizations have settings of their own.
      await client.refetchQueries({
        include: [MY_NOTIFICATION_PREFERENCE_CATALOG, MY_SUBSCRIBED_ORGANIZATIONS],
      });
    } catch {
      alerts.error(REFRESH_FAILED_COPY);
    }
  }, [alerts, client]);

  return useCallback(
    (change: Change) => {
      waiting.current += 1;
      queue.current = queue.current.then(async () => {
        waiting.current -= 1;
        const keys = keysOf(change.toggles);
        const confirmed =
          !(await opensConditions(change.draft, change.before)) || (await confirm());
        if (confirmed && (await write(change))) {
          awaitingRefresh.current.push(...keys);
        } else {
          apply(change.previous);
          release(keys);
        }
        if (waiting.current === 0 && awaitingRefresh.current.length > 0) {
          await refresh();
          release(awaitingRefresh.current.splice(0));
        }
      });
    },
    [apply, confirm, opensConditions, refresh, release, write]
  );
}

/**
 * One confirmation at a time, asked from inside the save queue. A panel that has
 * gone, before the question or while it was open, cannot be answered, so the change
 * is dropped and the reader told rather than the queue left waiting.
 */
function useConfirmation() {
  const alerts = useAlerts();
  const mounted = useRef(true);
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // A question nobody can answer any more is a no, or the queue waits forever.
      resolver.current?.(false);
      resolver.current = null;
    };
  }, []);

  const confirm: Confirm = useCallback(async () => {
    const confirmed =
      mounted.current &&
      (await new Promise<boolean>((resolve) => {
        resolver.current = resolve;
        setOpen(true);
      }));
    if (!mounted.current) {
      alerts.warning(
        "A change that turns on alerts with open issues wasn't saved, because you left before confirming it."
      );
    }
    return confirmed;
  }, [alerts]);

  const answer = useCallback((confirmed: boolean) => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setOpen(false);
  }, []);

  return { confirm, open, answer };
}

export function useAutosavingDraft({
  tenantId,
  catalog,
}: {
  tenantId: string | null;
  catalog: DraftCatalog | undefined;
}) {
  const { pendingKeys, hold, release } = usePendingKeys();
  const controller = useNotificationPreferenceDraft(catalog, pendingKeys.size > 0);
  const { draft, apply } = controller;
  const confirmation = useConfirmation();
  const enqueue = useSaveQueue({
    tenantId,
    catalog,
    apply,
    release,
    confirm: confirmation.confirm,
  });

  const change = useCallback(
    (next: NotificationPreferenceDraft) => {
      const toggles = dirtyToggles(next, draft);
      if (toggles.length === 0) {
        return;
      }
      const previous = toggles.flatMap(
        (toggle) => draft[draftKey(toggle.kind, toggle.channel)] ?? []
      );
      apply(toggles);
      hold(keysOf(toggles));
      enqueue({ toggles, previous, draft: next, before: draft });
    },
    [apply, draft, enqueue, hold]
  );

  const autosaving: NotificationPreferenceDraftController = useMemo(
    () => ({
      ...controller,
      setToggle: (kind, channel, enabled) => change(setDraftToggle(draft, kind, channel, enabled)),
      setEmailChoice: (kind, choice) => change(setDraftEmailChoice(draft, kind, choice)),
      setSectionChannel: (section, channel, enabled) =>
        change(setDraftSectionChannel(draft, section, channel, enabled)),
    }),
    [change, controller, draft]
  );

  const { answer } = confirmation;
  return {
    /** Every change made through this saves itself. */
    controller: autosaving,
    pendingKeys,
    saving: pendingKeys.size > 0 && !confirmation.open,
    confirmation: {
      open: confirmation.open,
      confirm: async () => answer(true),
      cancel: () => answer(false),
    },
  };
}
